import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MaxPlanSelectionModal } from "@features/MaxPlanSelectionModal/MaxPlanSelectionModal";
import { PlanChangedModal } from "@features/PlanChangedModal/PlanChangedModal";
import { useModalStore } from "@store/modal/modal";
import { useUpdateJointBillServiceProviderBillPlanMutation } from "@services/jointBillServiceProviderBillPlans/mutations";
import { PlanId } from "@services/jointBillServiceProviderBillPlans/types";
import { defaultPlansMetadata } from "@utils/plans/constants";
import { PlanCard } from "./PlanCard";

export interface IMainSelectionPlanCard {
  jointBillServiceId: number;
  jointBillServicePlanId: PlanId;
  price: number;
  discountedPrice?: number;
  discountedMonths?: number;
  discountExpirationDate?: Date;
  isSelected?: boolean;
  isDisabledForJointBillService?: boolean;
  onSuccess?: () => void;
  isPlanChangeAvailable?: boolean;
}

const planOrderClassNames: Record<string, string> = {
  [PlanId.FRE]: "order-0",
  [PlanId.FLX]: "order-1",
  [PlanId.MAX]: "order-2",
};

export const MainSelectionPlanCard: React.FC<IMainSelectionPlanCard> = (
  props,
) => {
  const {
    jointBillServiceId,
    jointBillServicePlanId,
    isDisabledForJointBillService,
    onSuccess,
    isPlanChangeAvailable,
  } = props;
  const { t } = useTranslation();
  const { create } = useModalStore();

  const { mutateAsync, isPending } =
    useUpdateJointBillServiceProviderBillPlanMutation({
      onSuccess: () => {
        onSuccess?.();
        toast.success(t("plan.toast.success"));
      },
    });

  const onOrderClick = async () => {
    if (!jointBillServiceId) return;

    if (isDisabledForJointBillService) {
      toast.error(t("plan.toast.planIsNotAvailable"));
      return;
    }

    // If client selected MAX plan, show modal with MAX plan options
    if (jointBillServicePlanId === PlanId.MAX) {
      create((modalProps) => (
        <MaxPlanSelectionModal
          {...modalProps}
          onSuccess={onSuccess}
          jointBillServiceId={jointBillServiceId}
        />
      ));
    } else {
      if (jointBillServicePlanId === PlanId.FLX) {
        create((modalProps) => (
          <PlanChangedModal
            {...modalProps}
            jointBillServiceId={jointBillServiceId}
            onAgreeClick={async () => {
              await mutateAsync({
                jointBillServiceId,
                jointBillServicePlanId,
              }).catch(() => {
                toast.error(t("toast.error"));
              });
            }}
          />
        ));
      } else {
        await mutateAsync({
          jointBillServiceId,
          jointBillServicePlanId,
        }).catch(() => {
          toast.error(t("toast.error"));
        });
      }
    }
  };

  const planMetadata = defaultPlansMetadata[jointBillServicePlanId];

  if (!planMetadata) {
    return null;
  }

  return (
    <PlanCard
      {...{
        ...props,
      }}
      {...planMetadata}
      onOrderClick={onOrderClick}
      isMutationPending={isPending}
      className={planOrderClassNames[jointBillServicePlanId]}
      isPlanChangeAvailable={isPlanChangeAvailable}
    />
  );
};
